import { useCallback, useMemo, useState, type CSSProperties } from 'react'
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { applyMove, type OgsmMove } from '@/features/ogsm/reorder'
import type { OgsmBoard } from '@/types/api'

export interface DragHandleBindings {
  ref: (element: HTMLElement | null) => void
  attributes: DraggableAttributes
  listeners: DraggableSyntheticListeners
}

export interface OgsmSortable {
  setNodeRef: (element: HTMLElement | null) => void
  style: CSSProperties
  isDragging: boolean
  handle: DragHandleBindings
}

/**
 * useSortable for one band or row. `disabled` is `!session.structureEditable`.
 * It stops dragging only; whether the node takes drops is its `SiblingList`'s call.
 */
export function useOgsmSortable(id: string, disabled: boolean): OgsmSortable {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: { draggable: disabled } })
  return {
    setNodeRef,
    style: { transform: CSS.Translate.toString(transform), transition },
    isDragging,
    handle: { ref: setActivatorNodeRef, attributes, listeners },
  }
}

export interface DroppedBoard {
  /** `board` with the last drop applied until `board` itself changes. */
  board: OgsmBoard
  /** Call from onDragEnd, so the new order commits with dnd-kit's own drop render. */
  drop: (move: OgsmMove) => void
}

/**
 * The cache's optimistic order reaches React a task after the drop (React Query notifies
 * on a timeout). A frame in the old order starts a transition back to the old slots that
 * the new order can't stop, so the siblings jump; this shows the drop in the same render.
 */
export function useDroppedBoard(board: OgsmBoard): DroppedBoard {
  const [dropped, setDropped] = useState<{
    base: OgsmBoard
    move: OgsmMove
  } | null>(null)
  // The cache has its own copy of the move (or its rollback) from here on.
  if (dropped && dropped.base !== board) setDropped(null)
  const shown = useMemo(
    () =>
      dropped && dropped.base === board
        ? applyMove(board, dropped.move)
        : board,
    [board, dropped]
  )
  const drop = useCallback(
    (move: OgsmMove) => setDropped({ base: board, move }),
    [board]
  )
  return { board: shown, drop }
}
